import Penpal, { IChildConnectionObject } from 'penpal';
// Import Config.
import { CONFIG_PENPAL_CONNECTION_TIMEOUT, CONFIG_ADITION } from '../../config';
// Import Interfaces.
import {
  _PermissionStatus,
  PermissionModel,
  AditionClientConfig,
  LayerParameters,
} from '../../interfaces';
// Import Services.
import * as Logger from '../../common/tools/logger';
import { AditionIframe } from './adition-iframe.interface';
import { PermissionLayer, PermissionLayerOptions } from '../../layer/interfaces';
import { CommunicationHandlerService } from './communication-handler.service';
import { Dimensions } from '../../client/interfaces';

/**
 * @class
 * @name AditionService
 * @description Class to handle the ADITION communication, function, etc..
 */
export class AditionService {

  /**
   * @var {string} moduleName Reflects the modules name.
   */
  private moduleName = `AditionService`;

  private communicationHandler: CommunicationHandlerService;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private childConnection: IChildConnectionObject<any>;

  /**
   * API exposed to the child (Permission Layer)
   */
  private childApi: Partial<PermissionLayer> = {
    connect: (options?: PermissionLayerOptions): void => {
      this.communicationHandler.openPermissionLayer(options);
    },

    update: (permissionType: string, status: _PermissionStatus): void => {
      this.communicationHandler.setPermissions([{
        usecase: permissionType,
        status,
        timestamp: (new Date()).toISOString()
      }]);
    },

    next: (): void => {
      return this.communicationHandler.updatePermissionLayer();
    },

    complete: (): void => {
      this.communicationHandler.closePermissionLayer();
      this.destroy();
    },

    getPermissionModel: (): Promise<PermissionModel> => {
      return new Promise((resolve): void => {
        resolve(this.communicationHandler.permissionModel);
      });
    },

    getParameters: (): Promise<LayerParameters> => {
      return this.communicationHandler.getLayerParameters();
    },

    resize: (dimensions: Dimensions): void => {
      this.communicationHandler.resizePermissionLayer(dimensions);
    }
  };

  private iframe: HTMLIFrameElement;

  /**
   * @public
   * @async
   * @function initialize
   * @description Function to initialize this module.
   * @param {string} permissionStates Reflects the permision states, which will be appended to the ADITION request.
   * @param communicationHandler
   * @return {Promise<void>}
   */
  public async initialize(permissionStates: string, communicationHandler: CommunicationHandlerService): Promise<void> {
    this.communicationHandler = communicationHandler;

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    return new Promise<void>(async (resolve: () => void) => {

      try {
        // Initialize and load the ADITION frame.
        await this.loadAditionFrame(permissionStates);

        // Log.
        Logger.debug(this.moduleName, `initialize`, `Module initialized.`);

        // Resolve.
        return resolve();
      } catch (error) {
        Logger.error(this.moduleName, `initialize`, error);
        return resolve();
      }
    });
  }

  /**
   * @private
   * @async
   * @function loadAditionFrame
   * @description Function to load a adition frame.
   * @param {string} permissionStates The string of current available permission states.
   * @return {Promise<void>}
   */
  private async loadAditionFrame(permissionStates: string): Promise<void> {

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    return new Promise<void>(async (resolve: () => void) => {

      try {
        const url: string = await this.buildAditionRequest(permissionStates);

        if (this.iframe) {
          this.iframe.src = url;
          Logger.debug(this.moduleName, `loadAditionFrame`, `Updated existing adition frame.`, url);
        } else {
          this.iframe = document.createElement('iframe');
          const iframeAttributes: AditionIframe = {
            border: '0',
            frameborder: '0',
            marginwidth: '0',
            marginheight: '0',
            allowtransparency: 'true',
            background: 'transparent',
            scrolling: 'no',
            style: 'display: block; width: 100%; height: 100%; border: 0;'
          };

          Object.keys(iframeAttributes).forEach((attr) => {
            this.iframe.setAttribute(attr, iframeAttributes[attr as keyof AditionIframe]);
          });

          this.childConnection = Penpal.connectToChild({
            url,
            iframe: this.iframe,
            appendTo: document.body,
            timeout: CONFIG_PENPAL_CONNECTION_TIMEOUT,
            methods: this.childApi,
          });

          await this.childConnection.promise;

          Logger.debug(this.moduleName, `loadAditionFrame`, `New adition frame created: `, url);
        }

        return resolve();
      } catch (error) {
        Logger.error(this.moduleName, `loadAditionFrame`, error);
        return resolve();
      }
    });
  }

  /**
   * @private
   * @async
   * @function buildAditionRequest
   * @description Function to build the adition request url (base url, cachebuster and required params).
   * @param {string} permissionStates The string of current available permission states.
   * @return {Promise<string>}
   */
  private async buildAditionRequest(permissionStates?: string): Promise<string> {

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type,@typescript-eslint/no-explicit-any
    return new Promise<string>(async(resolve: any) => {
      const clientConfig = await this.communicationHandler.getAditionClientConfig();

      // Example Request: https://plus.gmx.net/lt?wpt=x&nw=42&lt=portal(tam)category(homepage)section(homepage)tagid(top)layoutclass(b)&lang=de&wi=1424131313
      const aditionRequestURL: string = this.getEndpoint(clientConfig.portal) +
        '/lt?wpt=x&nw=42&lt=' +
        'portal('             + clientConfig.portal      + ')' +
        'category('           + clientConfig.category    + ')' +
        'section('            + clientConfig.section     + ')' +
        'tagid('              + clientConfig.tagid       + ')' +
        'layoutclass('        + clientConfig.layoutclass + ')' +
        (permissionStates && typeof permissionStates === 'string' ? permissionStates : '') +
        await this.getConsentParameters() +
        this.getCacheBuster();

      // Log.
      Logger.debug(this.moduleName, `buildAditionRequest`, `New adition request built: '${aditionRequestURL}'.`);

      // Resolve
      return resolve(aditionRequestURL);
    });
  }

  /**
   * @private
   * @function getCacheBuster
   * @description Function to get a random generated number to prevent caching, ready to be appended to the request.
   * @return {string}
   */
  private getCacheBuster(): string {
    return `&wi=${Math.floor(Math.random() * Date.now())}`;
  }

  /**
   * @private
   * @async
   * @function getParameters
   * @description Function to get custom consent parameters from the publishers page to be appended to the ADITION request.
   * @return {Promise<string>}
   */
  private async getConsentParameters(): Promise<string> {

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type,@typescript-eslint/no-explicit-any
    return new Promise<string>(async(resolve: any) => {

      const consentParameters = (await this.communicationHandler.getLayerParameters()).consentParameters;

      let consentParameterString = '';

      if (!consentParameters || Object.keys(consentParameters).length === 0) return resolve(consentParameterString);

      for (const _key in consentParameters) {
        consentParameterString = `${consentParameterString}&prf[${CONFIG_ADITION.PRF_PARAM_PREFIX.CONSENT_PARAMETER}_${_key}]=${encodeURIComponent(consentParameters[_key])}`;
      }

      return resolve(consentParameterString);
    });
  }

  /**
   * @private
   * @function getEndpoint
   * @description Function to get the ADITION endpoint from the given "portal" config.
   * @param {AditionClientConfig['portal']} portal ADITION "portal" parameter.
   * @return {string}
   */
  private getEndpoint(portal: AditionClientConfig['portal']): string {
    return CONFIG_ADITION.ENDPOINT[portal] || CONFIG_ADITION.DEFAULT.ENDPOINT;
  }

  public destroy(): void {
    if (this.childConnection) {
      this.childConnection.destroy();
      this.childConnection = null;
    }

    if (this.iframe) {
      this.iframe.src = 'about:blank';
      if (this.iframe.parentNode) {
        this.iframe.parentNode.removeChild(this.iframe);
      }
      this.iframe = null;
    }

    this.communicationHandler = null;
  }
}
